/**
 * Cosmos v2 About Us — published at /about (canonical) with /about-new
 * kept as a redirect alias.
 *
 * Replaces the old About Us layout with the new design (see Canva mockup):
 *   1. NewNavBar (shared with the homepage so the brand + auth modals are consistent)
 *   2. Hero        — "ABOUT US" pill + italicised "university IP" headline + mission paragraphs
 *   3. Newsletter  — "Sign up to The Cosmos" inline subscribe (reuses apiService.submitNewsletterSignup)
 *   4. Team        — Parag, Makarand, Pranav, Yukta with LinkedIn links
 *   5. Advisory    — 7 advisors in a 1/2/3-column grid
 *   6. Get In Touch — concierge form (apiService.createConciergeCall, tagged "Others")
 *   7. NewFooter   — shared with the homepage
 *
 * Mobile-first: every section stacks below `sm` and the form layout collapses
 * to one column. PostHog tracking fires on every CTA / link / submit so we
 * can see which sections drive engagement.
 */

import React, { useEffect, useState } from 'react';
import { NewNavBar, NavItem, NewFooter } from './NewHome';
import { PrivacyPolicyModal } from './PrivacyPolicyModal';
import { TermsOfServiceModal } from './TermsOfServiceModal';
import { WhitePaperModal } from './WhitePaperModal';
import LoginModal from './LoginModal';
import SignUpModal from './SignUpModal';
import { SignupRequiredModal } from '../../components/SignupRequiredModal';
import { trackClick } from '../../utils/analytics';
import { apiService } from '../../services/apiService';

// ---------------------------------------------------------------------------
// Static data — kept inline so the page is self-contained.
// ---------------------------------------------------------------------------

interface TeamMember {
  name: string;
  role: string;
  description: string;
  linkedinUrl: string;
  imageUrl: string;
}

interface AdvisoryMember {
  name: string;
  location: string;
  description: string;
  imageUrl: string;
}

const TEAM: TeamMember[] = [
  {
    name: 'Parag Agarwal',
    role: 'CEO / Product',
    description: '20 years in healthcare, innovation, and tech transfer across Scotland, Singapore, India',
    linkedinUrl: 'https://www.linkedin.com/in/paragis/',
    imageUrl: '/images/team/parag.jpg',
  },
  {
    name: 'Makarand Kaprekar',
    role: 'Commercial Head',
    description: '28+ years in senior business roles in MNCs. Exec, leadership coach to corporates and MSMEs.',
    linkedinUrl: 'https://www.linkedin.com/in/makarandkaprekar/',
    imageUrl: '/images/team/makarand.jpg',
  },
  {
    name: 'Pranav Garg',
    role: 'Tech Lead',
    description: 'AI/ML and Python developer, has successfully worked on over 25+ LLM projects over 5 years.',
    linkedinUrl: 'https://www.linkedin.com/in/prgarg007/',
    imageUrl: '/images/team/pranav.png',
  },
  {
    name: 'Yukta Karkera',
    role: 'Product Head',
    description: 'Engineer and designer with experience across healthcare, fintech, and edtech in the USA and India.',
    linkedinUrl: 'https://www.linkedin.com/in/yuktakarkera/',
    imageUrl: '/images/team/yukta.png',
  },
];

const ADVISORS: AdvisoryMember[] = [
  {
    name: 'Srikanth Sundararajan PhD',
    location: 'India',
    description: 'A veteran IITian — Innovator, Investor and Business Mentor with 35+ years global IT exp.',
    imageUrl: '/images/advisory-board/srikanth.jpg',
  },
  {
    name: 'Dinesh Nambiar PhD',
    location: 'India',
    description: '30+ years heading R&D and quality management in Pharma & FMCG',
    imageUrl: '/images/advisory-board/dinesh.jpg',
  },
  {
    name: 'Darshan Gangoli',
    location: 'India',
    description: 'IP based SME IPOs, 30+ years financial services, real estate, infra development',
    imageUrl: '/images/advisory-board/darshan.jpg',
  },
  {
    name: 'Rishikesh Trivedi',
    location: 'UAE',
    description: '20+ years in tech investments and 15+ in strategy, marketing, alliances & sales',
    imageUrl: '/images/advisory-board/rishikesh.jpg',
  },
  {
    name: 'Darren Griffin',
    location: 'UK',
    description: 'World-renowned geneticist, bioscientist with 30+ years industry liaison & science communications experience',
    imageUrl: '/images/advisory-board/darren.png',
  },
  {
    name: 'Deborah Whittaker',
    location: 'UK',
    description: 'PhD researcher in environmental biotech, academic board member, specialist in water quality',
    imageUrl: '/images/advisory-board/deborah.jpg',
  },
  {
    name: 'Jateen Kore',
    location: 'India',
    description: '25+ years in digital and direct marketing. Ex leadership at Vodafone and IDFC Bank',
    imageUrl: '/images/advisory-board/jateen.jpg',
  },
];

// ---------------------------------------------------------------------------
// Reusable icon
// ---------------------------------------------------------------------------

const LinkedInIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Small atoms
// ---------------------------------------------------------------------------

/** Section header with the gold underline that matches the design mock. */
const SectionTitle: React.FC<{ children: React.ReactNode; eventName: string }> = ({ children, eventName }) => {
  useEffect(() => { trackClick(`about_section_view_${eventName}`); }, [eventName]);
  return (
    <div className="mb-6 sm:mb-8">
      <h2 className="text-xs sm:text-sm font-bold tracking-[0.25em] text-primary-700 uppercase">
        {children}
      </h2>
      <div className="mt-2 h-px w-24 sm:w-32 bg-secondary-og-600" />
    </div>
  );
};

const Initials: React.FC<{ name: string; className?: string }> = ({ name, className }) => (
  <div className={`flex items-center justify-center font-semibold text-primary-700 ${className || ''}`}>
    {name.split(' ').filter(Boolean).map(n => n[0]).slice(0, 2).join('')}
  </div>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface AboutUsNewProps {
  onLogin: (email: string) => void;
}

const AboutUsNew: React.FC<AboutUsNewProps> = ({ onLogin }) => {
  // ---- Auth modals --------------------------------------------------------
  //
  // Previously the "Log In / Sign Up" button on this page redirected to
  // `/#login` on the homepage, which actually just took the user back to the
  // landing page without ever opening the modal — confusing UX. Now we mount
  // the same LoginModal / SignUpModal stack the homepage uses so login works
  // in-place. On successful login the parent's `onLogin` callback fires
  // (currently a hard navigation to `/` so MainWithClerk can pick up the
  // freshly stored session and route the user into the app).
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showSignupRequiredModal, setShowSignupRequiredModal] = useState(false);

  const closeAllAuthModals = () => {
    setShowLoginModal(false);
    setShowSignUpModal(false);
    setShowSignupRequiredModal(false);
  };

  const openLogin = () => { trackClick('about_open_login'); closeAllAuthModals(); setShowLoginModal(true); };
  const openSignUp = () => { trackClick('about_open_signup'); closeAllAuthModals(); setShowSignUpModal(true); };
  const handleSignUpSuccess = () => {
    setShowSignUpModal(false);
    setShowLoginModal(true);
  };

  const handleLoginClick = () => {
    trackClick('about_nav_login_signup');
    openLogin();
  };

  const navItems: NavItem[] = [
    { id: 'about_us', label: 'About Us', href: '#', onClick: () => trackClick('about_nav_about_us') },
  ];

  // ---- Page-view tracking -------------------------------------------------
  useEffect(() => { trackClick('about_page_view'); }, []);

  // ---- Privacy / Terms / White Paper modals (shared with the homepage) ----
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showWhitePaperModal, setShowWhitePaperModal] = useState(false);

  // ---- Newsletter signup --------------------------------------------------
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterState, setNewsletterState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [newsletterError, setNewsletterError] = useState('');
  const submitNewsletter = async () => {
    trackClick('about_newsletter_subscribe', { email: newsletterEmail });
    if (!newsletterEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newsletterEmail)) {
      setNewsletterState('error');
      setNewsletterError('Please enter a valid email.');
      return;
    }
    setNewsletterState('submitting');
    setNewsletterError('');
    try {
      const res = await (apiService as any).submitNewsletterSignup?.(newsletterEmail, 'about_us_newsletter');
      if (res?.success) {
        setNewsletterState('success');
        setNewsletterEmail('');
        trackClick('about_newsletter_subscribe_success', { already_subscribed: !!res?.already_subscribed });
      } else {
        setNewsletterState('error');
        setNewsletterError(res?.error || 'Something went wrong, please try again.');
        trackClick('about_newsletter_subscribe_error', { error: res?.error });
      }
    } catch (e: any) {
      setNewsletterState('error');
      setNewsletterError(e?.message || 'Something went wrong, please try again.');
      trackClick('about_newsletter_subscribe_error', { error: e?.message });
    }
  };

  // ---- Get In Touch concierge form ----------------------------------------
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactState, setContactState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');
  const contactValid = contactForm.name.trim() && contactForm.email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email) && contactForm.message.trim();
  const submitContact = async () => {
    trackClick('about_contact_submit', { email: contactForm.email });
    if (!contactValid) {
      setContactState('error');
      setContactError('Please fill in name, email and your enquiry.');
      return;
    }
    setContactState('submitting');
    setContactError('');
    try {
      const res = await apiService.createConciergeCall({
        name: contactForm.name.trim(),
        email: contactForm.email.trim().toLowerCase(),
        // Tagged "Others" so concierge ops can route from About Us specifically.
        areas: ['Others'],
        message: contactForm.message.trim(),
        // Human-readable lead source so the inbox routing knows it came from
        // the About Us contact form (vs the in-app SupportModal, etc).
        form_source: 'about_us_contact_form',
      });
      if (res?.success) {
        setContactState('success');
        trackClick('about_contact_submit_success', { email: contactForm.email });
      } else {
        setContactState('error');
        setContactError(res?.error || 'Submission failed. Please try again.');
        trackClick('about_contact_submit_error', { error: res?.error });
      }
    } catch (e: any) {
      setContactState('error');
      setContactError(e?.message || 'Submission failed. Please try again.');
      trackClick('about_contact_submit_error', { error: e?.message });
    }
  };

  return (
    <div className="bg-white min-h-screen font-dm-sans text-grayscale-1200 overflow-x-hidden">
      <NewNavBar
        navItems={navItems}
        onLoginClick={handleLoginClick}
        onLogoClick={() => { trackClick('about_nav_logo'); window.location.href = '/'; }}
      />

      {/* Horizontal padding matches the homepage's NavBar / Footer chrome
          (`px-4 sm:px-6 lg:px-8`) so the editorial column lines up with the
          brand-bar above and the footer below. */}
      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* -------- Hero -------- */}
        <section className="text-center pt-6 sm:pt-10 lg:pt-12 pb-6 sm:pb-8">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-secondary-100 border border-secondary-400 text-secondary-og-600 text-xs sm:text-sm font-bold tracking-wide">
            ABOUT US
          </div>
          <h1 className="mt-5 sm:mt-7 text-3xl sm:text-5xl lg:text-6xl font-bold text-grayscale-1300 leading-tight px-3">
            Simplifying how <span className="italic text-secondary-og-600">university IP</span>
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>meets the world
          </h1>
          <div className="mt-6 sm:mt-8 max-w-3xl mx-auto px-2 text-sm sm:text-base text-grayscale-1200 leading-relaxed space-y-4">
            <p>
              Our mission is to simplify and speed up technology transfer. We are disrupting how
              university IP is marketed, discovered and eventually commercialised. Cosmos, our first
              product, allows you to search and match with licensable tech to grow your business,
              improve efficiency or lower operational costs.
            </p>
            <p>
              <a
                href="https://universitytech.io"
                onClick={() => trackClick('about_hero_link_universitytech')}
                className="underline text-grayscale-1300 hover:text-primary-600 transition-colors"
              >
                UniversityTech.io
              </a>{' '}
              is brought to you by Paragise Private Limited, incorporated in Mumbai in June 2024.
              We are proudly incubated at IIT Bombay (SINE).
            </p>
          </div>
        </section>

        {/* -------- Newsletter -------- */}
        <section className="py-5 sm:py-7">
          <SectionTitle eventName="newsletter">Newsletter</SectionTitle>
          <div className="rounded-2xl border border-primary-300 bg-white p-5 sm:p-7 w-full">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-base sm:text-lg font-semibold text-grayscale-1300">The Cosmos</h3>
              <svg className="w-4 h-4 text-secondary-og-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M22 2L11 13M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="text-xs sm:text-sm text-grayscale-1100 mb-4">
              Monthly updates on technology transfer, university IP, and the Cosmos product. No spam.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={newsletterEmail}
                onChange={e => { setNewsletterEmail(e.target.value); if (newsletterState !== 'idle') setNewsletterState('idle'); }}
                onKeyDown={e => { if (e.key === 'Enter') submitNewsletter(); }}
                placeholder="your@email.com"
                className="flex-1 px-3 py-2.5 border border-grayscale-300 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                aria-label="Email for newsletter"
              />
              <button
                onClick={submitNewsletter}
                disabled={newsletterState === 'submitting'}
                className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-grayscale-300 text-white text-sm font-medium whitespace-nowrap"
              >{newsletterState === 'submitting' ? '…' : 'Subscribe'}</button>
            </div>
            {newsletterState === 'success' && (
              <p className="mt-2 text-xs text-green-600">You're subscribed!</p>
            )}
            {newsletterState === 'error' && (
              <p className="mt-2 text-xs text-red-600">{newsletterError}</p>
            )}
          </div>
        </section>

        {/* -------- Meet Our Team -------- */}
        <section className="py-5 sm:py-7">
          <SectionTitle eventName="team">Meet Our Team</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {TEAM.map(m => (
              <article
                key={m.name}
                className="rounded-2xl border border-grayscale-200 bg-white p-5 sm:p-6 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-primary-100 mb-3 ring-2 ring-primary-200">
                  <img
                    src={m.imageUrl}
                    alt={m.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const t = e.target as HTMLImageElement;
                      t.style.display = 'none';
                      (t.nextElementSibling as HTMLElement | null)?.classList.remove('hidden');
                    }}
                  />
                  <Initials name={m.name} className="hidden w-full h-full text-2xl" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-grayscale-1300 leading-tight">{m.name}</h3>
                <p className="mt-1 text-xs sm:text-sm font-medium text-primary-700">{m.role}</p>
                <p className="mt-2 text-[11px] sm:text-xs text-grayscale-1100 leading-relaxed flex-1">{m.description}</p>
                <a
                  href={m.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick('about_team_linkedin', { name: m.name })}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:text-primary-600 transition-colors"
                  aria-label={`Connect with ${m.name} on LinkedIn`}
                >
                  <LinkedInIcon className="w-3.5 h-3.5" />
                  <span>Connect on LinkedIn</span>
                </a>
              </article>
            ))}
          </div>
        </section>

        {/* -------- Advisory Board -------- */}
        <section className="py-5 sm:py-7">
          <SectionTitle eventName="advisory">Advisory Board</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ADVISORS.map(a => (
              <article
                key={a.name}
                className="rounded-xl border border-grayscale-200 bg-white p-4 flex items-start gap-3 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-full overflow-hidden bg-primary-100 shrink-0">
                  <img
                    src={a.imageUrl}
                    alt={a.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const t = e.target as HTMLImageElement;
                      t.style.display = 'none';
                      (t.nextElementSibling as HTMLElement | null)?.classList.remove('hidden');
                    }}
                  />
                  <Initials name={a.name} className="hidden w-full h-full text-xs" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-xs sm:text-sm font-bold text-grayscale-1300 leading-tight">{a.name}</h3>
                  <p className="mt-0.5 text-[11px] sm:text-xs text-secondary-og-600 font-medium">{a.location}</p>
                  <p className="mt-1.5 text-[11px] sm:text-xs text-grayscale-1100 leading-snug">{a.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* -------- Get In Touch -------- */}
        <section className="py-5 sm:py-7">
          <SectionTitle eventName="get_in_touch">Get In Touch</SectionTitle>
          <div className="rounded-2xl border border-primary-300 bg-white p-5 sm:p-7 w-full">
            {contactState === 'success' ? (
              <div className="text-center py-6">
                <div className="text-3xl mb-2">✓</div>
                <h3 className="text-base sm:text-lg font-bold text-grayscale-1300 mb-2">Thanks — your enquiry is in.</h3>
                <p className="text-sm text-grayscale-1100">We'll get back to you within 48 hours.</p>
              </div>
            ) : (
              <>
                <p className="text-sm font-semibold text-grayscale-1300 mb-4">
                  Tell us what you're looking for and our team will get back to you shortly.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-grayscale-1100 mb-1">Name</label>
                    <input
                      type="text"
                      value={contactForm.name}
                      onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Your full name"
                      className="w-full px-3 py-2.5 border border-grayscale-300 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-grayscale-1100 mb-1">Email</label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="you@company.com"
                      className="w-full px-3 py-2.5 border border-grayscale-300 rounded-lg text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-grayscale-1100 mb-1">Enquiry</label>
                    <textarea
                      value={contactForm.message}
                      onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))}
                      placeholder="Tell us about your interest — investment, partnership, licensing, feedback or something else…"
                      rows={4}
                      className="w-full px-3 py-2.5 border border-grayscale-300 rounded-lg text-sm focus:outline-none focus:border-primary-500 resize-y"
                    />
                  </div>
                  {contactState === 'error' && (
                    <div className="text-xs text-red-600">{contactError}</div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
                    <span className="text-[11px] text-grayscale-1100 inline-flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      We respond within 48 hours
                    </span>
                    <button
                      onClick={submitContact}
                      disabled={contactState === 'submitting'}
                      className="px-5 py-2.5 rounded-full bg-primary-600 hover:bg-primary-700 disabled:bg-grayscale-300 text-white text-sm font-medium inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
                    >
                      <span>{contactState === 'submitting' ? 'Sending…' : 'Send enquiry'}</span>
                      <span aria-hidden="true">→</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </main>

      <NewFooter
        onShowPrivacy={() => { trackClick('about_footer_privacy'); setShowPrivacyModal(true); }}
        onShowTerms={() => { trackClick('about_footer_terms'); setShowTermsModal(true); }}
        onShowRdSymposium={() => { trackClick('about_footer_rd_symposium'); window.location.href = '/rd-symposium'; }}
        onShowWhitePaper={() => { trackClick('about_footer_whitepaper'); setShowWhitePaperModal(true); }}
        onShowUniversities={() => { trackClick('about_footer_universities'); window.location.href = '/universities'; }}
        onShowComet={() => { trackClick('about_footer_comet'); window.location.href = '/comet'; }}
      />

      <WhitePaperModal
        isOpen={showWhitePaperModal}
        onClose={() => setShowWhitePaperModal(false)}
        formSource="about_footer_whitepaper_modal"
      />

      {showPrivacyModal && (
        <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      )}
      {showTermsModal && (
        <TermsOfServiceModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
      )}

      {/* Auth modals — same stack the homepage uses so login/sign-up works
          in-place rather than redirecting away from About Us. */}
      {showLoginModal && (
        <LoginModal
          onClose={closeAllAuthModals}
          onLogin={onLogin}
          onSignUpClick={openSignUp}
        />
      )}
      {showSignUpModal && (
        <SignUpModal
          onClose={closeAllAuthModals}
          onSignupSuccess={handleSignUpSuccess}
        />
      )}
      {showSignupRequiredModal && (
        <SignupRequiredModal
          isOpen={showSignupRequiredModal}
          onClose={closeAllAuthModals}
          onLogin={openLogin}
          onSignup={openSignUp}
        />
      )}
    </div>
  );
};

export default AboutUsNew;
